import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { PayrollService } from 'src/app/services/payroll.service';
import { UtilsService } from 'src/app/services/utils.service';

type TabKey = 'all' | 'pending' | 'stamped' | 'error';

interface StatusMeta {
    label: string;
    bg: string;
    color: string;
}

const STATUS_META: { [key: string]: StatusMeta } = {
    stamped: { label: 'Timbrado', bg: '#e3fbe8', color: '#3c7e43' },
    pending: { label: 'Pendiente', bg: '#fef3d4', color: '#cf8515' },
    error: { label: 'Error', bg: '#ffccce', color: '#c5010b' }
};

@Component({
    selector: 'app-payroll-list',
    templateUrl: './payroll-list.component.html',
    styleUrls: ['./payroll-list.component.css']
})
export class PayrollListComponent implements OnInit {

    employeeId!: number;
    employee: any = null;

    /** Set completo obtenido del backend (respetando solo el filtro de periodo), usado para calcular KPIs y conteos de tabs sin refetch al cambiar de tab. */
    private allPayrolls: any[] = [];
    public rowData: any[] = [];

    activeTab: TabKey = 'all';
    filterPeriod = '';

    loading = false;
    loadError = false;

    gridContext = { componentParent: this };
    private gridApi!: GridApi;

    columnDefs = [
        {
            field: 'payroll_number', headerName: 'Colaborador', flex: 1.3, sortable: false, filter: false,
            cellRenderer: () => `<a href="javascript:void(0)" class="payroll-name-link">${this.getEmployeeName()}</a>`
        },
        {
            field: 'period_start', headerName: 'Periodo de pago', flex: 1.1, sortable: false, filter: false,
            valueGetter: (params: any) => this.formatPeriod(params.data)
        },
        {
            field: 'net_paid', headerName: 'Neto a pagar', flex: 0.9, sortable: false, filter: false,
            valueFormatter: (params: any) => this.formatCurrency(params.value)
        },
        {
            field: 'cfdi_status', headerName: 'Estado CFDI', flex: 0.9, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderStatusBadge(params.value)
        },
        {
            field: 'certification_datetime', headerName: 'Fecha timbrado', flex: 1, sortable: false, filter: false,
            valueFormatter: (params: any) => params.value ? this.formatDate(params.value) : '--/--/----'
        },
        {
            headerName: 'Acciones', width: 100, sortable: false, filter: false, resizable: false,
            cellRenderer: () => `<i class="ti ti-eye payroll-action-icon" title="Ver recibo"></i>`
        }
    ];

    public defaultColDef = {
        sortable: false,
        filter: false,
        resizable: true,
    };

    public noRowsTemplate = '<span style="padding: 24px; color: #6b7280;">No se encontraron recibos de nómina</span>';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private employeeService: EmployeeService,
        private payrollService: PayrollService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService
    ) { }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            const idParam = params.get('id');
            this.employeeId = idParam ? parseInt(idParam, 10) : 0;
            if (!this.employeeId) return;
            this.loadEmployee();
            this.fetchPayrolls();
        });
    }

    loadEmployee(): void {
        this.employeeService.getById(this.employeeId).subscribe({
            next: (resp: any) => { this.employee = resp; },
            error: () => { this.employee = null; }
        });
    }

    fetchPayrolls(): void {
        this.loading = true;
        this.loadError = false;
        this.spinner.show();
        const params: any = {};
        if (this.filterPeriod) params.period = this.filterPeriod;

        this.payrollService.listPayrolls(this.employeeId, params).subscribe({
            next: (resp: any) => {
                this.allPayrolls = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                this.applyTabFilter();
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.allPayrolls = [];
                this.rowData = [];
                this.loading = false;
                this.loadError = true;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de recibos de nómina', icon: 'error' });
            }
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
    }

    setTab(tab: TabKey): void {
        this.activeTab = tab;
        this.applyTabFilter();
    }

    applyTabFilter(): void {
        if (this.activeTab === 'all') {
            this.rowData = [...this.allPayrolls];
        } else {
            this.rowData = this.allPayrolls.filter((p) => p.cfdi_status === this.activeTab);
        }
    }

    onPeriodFilterChanged(): void {
        this.fetchPayrolls();
    }

    onCellClicked(event: any): void {
        if (event?.data?.id) {
            this.router.navigateByUrl(`/employee/${this.employeeId}/payroll/${event.data.id}`);
        }
    }

    goBack(): void {
        this.router.navigateByUrl(`/employee/${this.employeeId}`);
    }

    // ---- KPIs ----
    get totalCount(): number { return this.allPayrolls.length; }
    get stampedCount(): number { return this.allPayrolls.filter((p) => p.cfdi_status === 'stamped').length; }
    get pendingCount(): number { return this.allPayrolls.filter((p) => p.cfdi_status === 'pending').length; }
    get errorCount(): number { return this.allPayrolls.filter((p) => p.cfdi_status === 'error').length; }

    get stampedPercent(): string { return this.pct(this.stampedCount); }
    get pendingPercent(): string { return this.pct(this.pendingCount); }
    get errorPercent(): string { return this.pct(this.errorCount); }

    private pct(count: number): string {
        if (!this.totalCount) return '0.0';
        return ((count / this.totalCount) * 100).toFixed(1);
    }

    get netPeriodTotal(): number {
        return this.allPayrolls.reduce((sum, p) => sum + (Number(p.net_paid) || 0), 0);
    }

    // ---- Helpers ----
    getEmployeeName(): string {
        if (!this.employee) return '--';
        const parts = [this.employee.first_name, this.employee.last_name, this.employee.mother_last_name].filter(Boolean);
        return parts.join(' ') || '--';
    }

    formatPeriod(data: any): string {
        if (!data) return '--';
        return `${this.formatDate(data.period_start)} al ${this.formatDate(data.period_end)}`;
    }

    formatCurrency(value: any): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    renderStatusBadge(value: string): string {
        const meta = STATUS_META[value];
        if (!meta) return '';
        return `<span class="payroll-status-badge" style="background:${meta.bg};color:${meta.color}">${meta.label}</span>`;
    }
}
